import ApiService from '@/services/ApiService';

class UserService {
  static async getPermissions() {
    const composedURL = `/api3/users/permissions?_ts=${Date.now()}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getUserMeData() {
    const composedURL = `/api3/users/me?_ts=${Date.now()}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getConfigSetting() {
    const composedURL = `/api3/users/config?_ts=${Date.now()}`;
    const response = await ApiService.get(composedURL);
    localStorage.setItem('tokkoChatHost', response['tokko-messenger-url']);
    return response;
  }

  static async getSupervisionLocks() {
    const composedURL = `/api3/users/me/supervision_locks`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getBadgesCounters() {
    const composedURL = `/api3/users/badges`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async postConfigSetting(requestData) {
    const composedURL = `/api3/users/config`;
    const response = await ApiService.post(composedURL, requestData);
    return response;
  }

  static async postCurrentFavoriteTab(requestData) {
    const composedURL = `/api3/users/set_current_favourite_tab`;
    const response = await ApiService.post(composedURL, requestData);
    return response;
  }

  static async postSearchParameters(requestData) {
    const composedURL = `/api3/users/search_parameters`;
    const response = await ApiService.post(composedURL, requestData);
    return response;
  }

  static async getCompanyConfig() {
    const composedURL = `/api3/company/config?_ts=${Date.now()}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async putUserModals(userModals) {
    const composedURL = `/api3/users/user_modals`;
    const requestBody = userModals;
    const response = await ApiService.put(composedURL, requestBody);
    return response;
  }

  static async getUserHistoryFilters() {
    const composedURL = `/api3/users/history/filters`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  // noRedirect: a 403 here means "can't see this user's history", not "session
  // gone", so it must reach the UI instead of bouncing to /not_connected.
  static async getUserHistory({ userId, filterId, cursor }) {
    const params = new URLSearchParams({ filter_id: filterId });
    if (cursor) params.set('cursor', cursor);
    const composedURL = `/api3/users/${userId}/history?${params}`;
    const response = await ApiService.get(composedURL, undefined, true);
    return response;
  }

  // noRedirect: a 403 means the requester is not a company admin, not a lost
  // session, so it must reach the UI instead of bouncing to /not_connected.
  static async getUserPermissions(userId) {
    const composedURL = `/api3/users/${userId}/permissions`;
    const response = await ApiService.get(composedURL, undefined, true);
    return response;
  }

  // noRedirect: a 403 means the requester can't edit this user's permissions,
  // not a lost session, so it must reach the UI instead of bouncing.
  static async updateUserPermissions({ userId, changes }) {
    const composedURL = `/api3/users/${userId}/permissions`;
    const response = await ApiService.put(
      composedURL,
      changes,
      undefined,
      false,
      true
    );
    return response;
  }

  static async getUserById(id) {
    const composedURL = `/api3/users/${id}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getUserProfile(id) {
    const composedURL = `/api3/users/${id}/profile`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  // noRedirect: a 403 means a sent field is not editable by the requester,
  // not a lost session, so it must reach the UI instead of bouncing.
  static async updateUserProfile({ userId, changes }) {
    const composedURL = `/api3/users/${userId}/profile`;
    const response = await ApiService.patch(
      composedURL,
      changes,
      undefined,
      true
    );
    return response;
  }

  // noRedirect: a 403 means the target is the account owner (deactivate) or
  // the plan user limit was reached (reactivate), not a lost session.
  static async setUserActive({ userId, isActive }) {
    const action = isActive ? 'reactivate' : 'deactivate';
    const composedURL = `/api3/user/${userId}/${action}`;
    const response = await ApiService.post(
      composedURL,
      undefined,
      undefined,
      false,
      true
    );
    return response;
  }

  // Both enqueue a background job and answer 202. Properties can only leave an
  // agent that is already inactive. noRedirect: a 4xx carries the validation
  // error, not a lost session.
  static async transferAgentProperties({ oldAgentId, newAgentId }) {
    const composedURL = `/api3/users/transfer/properties`;
    const body = { old_agent_id: oldAgentId, new_agent_id: newAgentId };
    const response = await ApiService.post(
      composedURL,
      body,
      undefined,
      false,
      true
    );
    return response;
  }

  static async transferAgentContacts({ oldAgentId, newAgentId }) {
    const composedURL = `/api3/users/transfer/contacts`;
    const body = { old_agent_id: oldAgentId, new_agent_id: newAgentId };
    const response = await ApiService.post(
      composedURL,
      body,
      undefined,
      false,
      true
    );
    return response;
  }

  // Answers `pending`, `done` or `rejected`. noRedirect: a 404 means the job
  // is no longer tracked, not a lost session.
  static async getAgentTransferStatus(taskId) {
    const composedURL = `/api3/users/transfer/${taskId}/status`;
    const response = await ApiService.get(composedURL, undefined, true);
    return response;
  }

  // Always targets the authenticated user. noRedirect: a 400 carries the
  // validation message (type/size/corrupt file) and must reach the UI.
  static async uploadProfilePicture(file) {
    const composedURL = `/api3/users/profile-picture/`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await ApiService.post(
      composedURL,
      formData,
      undefined,
      true,
      true
    );
    return response;
  }

  // Always targets the authenticated user. Responds with the default avatar
  // URL, not null.
  static async deleteProfilePicture() {
    const composedURL = `/api3/users/profile-picture/`;
    const response = await ApiService.delete(
      composedURL,
      undefined,
      undefined,
      true
    );
    return response;
  }

  // noRedirect: a 403 means the requester can't change this user's password
  // (e.g. an admin targeting another admin), not a lost session.
  static async changePassword({ userId, oldPassword, newPassword }) {
    const composedURL = `/api3/users/${userId}/change_password`;
    const body = oldPassword
      ? { old_password: oldPassword, new_password: newPassword }
      : { new_password: newPassword };
    const response = await ApiService.post(
      composedURL,
      body,
      undefined,
      false,
      true
    );
    return response;
  }
}

export default UserService;
